import cloneDeep from 'lodash/cloneDeep';
import remove from 'lodash/remove';
import api from '../../store/config/api';
import { updateProcess } from './process';
import { updateStep } from './step';
import { FIELD, STEP, PROCESS, getEntityByID } from '../../schemas';
import { findFieldByDisplayName } from '../../util/fields';
import { PUBLIC } from '../../util/permissions';
import { format } from '../../util/url';
import { toast } from '../../actions/toast';

// Add Field
export const [
  ADD_FIELD,
  ADD_FIELD_REQUEST,
  ADD_FIELD_SUCCESS,
  ADD_FIELD_FAILURE
] = api.buildTypes('ADD_FIELD');

export const addAndAttachWritableField = dispatch => (
  displayName,
  process,
  step,
  fields,
  allProcessFields,
  { processID, teamName, fieldAddRequestFailure, fieldAddRequestSuccess }
) => {
  const { stepName } = step || {};
  const {
    processName,
    fieldVisibility: visibility = PUBLIC,
    fieldVisibleToUsers: visibleToUsers = []
  } =
    process || {};
  const field = {
    displayName,
    teamName,
    processID: processName,
    visibility,
    visibleToUsers
  };

  const existingFieldOnThisEntity = findFieldByDisplayName(field, fields);
  if (existingFieldOnThisEntity) {
    return dispatch(toast({ message: fieldAddRequestSuccess }));
  }

  let action;
  const existingFieldElsewhereOnProcess = findFieldByDisplayName(
    field,
    allProcessFields
  );
  if (existingFieldElsewhereOnProcess) {
    const { fieldName } = existingFieldElsewhereOnProcess;
    if (stepName) {
      action = appendFieldToStepWritableFields(step, process, fieldName);
    } else {
      action = appendFieldToProcessWritableFields(process, fieldName);
    }
  } else {
    action = addField(field, processID, stepName);
  }
  return dispatch(action)
    .then(() => dispatch(toast({ message: fieldAddRequestSuccess })))
    .catch(error =>
      dispatch(toast({ message: fieldAddRequestFailure, error }))
    );
};

export function addField(field, processID, stepName) {
  return (dispatch, getState) => {
    const { teamName } = field; // eslint-disable-line no-unused-vars
    return dispatch(addFieldEntity(field)).then(({ response }) => {
      const { fieldName } = response.entities.fields[response.result];
      const state = getState();
      const process = getEntityByID(state.entities.processes, PROCESS, {
        processID
      });
      if (stepName) {
        const step = getEntityByID(state.entities.steps, STEP, {
          processID,
          stepName
        });
        dispatch(appendFieldToStepWritableFields(step, process, fieldName));
      } else if (processID) {
        dispatch(appendFieldToProcessWritableFields(process, fieldName));
      }
    });
  };
}

export function addFieldEntity(field) {
  return {
    type: ADD_FIELD,
    payload: {
      url: apiPathForField(field),
      schema: FIELD,
      options: {
        method: 'POST',
        body: field
      }
    }
  };
}

export const appendFieldToProcessWritableFields = (process, fieldName) => {
  const {
    teamName,
    processID,
    writableFields: previousWritableFields = []
  } = process;
  return updateProcess({
    teamName,
    processID,
    data: { writableFields: previousWritableFields.concat([fieldName]) }
  });
};

export const appendFieldToStepWritableFields = (step, process, fieldName) => {
  const { processID } = process;
  const {
    teamName,
    stepName,
    writableFields: previousWritableFields = []
  } = step;
  return updateStep({
    teamName,
    processName: processID,
    stepName,
    step: { writableFields: previousWritableFields.concat([fieldName]) }
  });
};

// Update Field
export const [
  UPDATE_FIELD,
  UPDATE_FIELD_REQUEST,
  UPDATE_FIELD_SUCCESS,
  UPDATE_FIELD_FAILURE
] = api.buildTypes('UPDATE_FIELD');

export function updateField(field) {
  const { fieldName, retroactive } = field;
  const basePath = apiPathForField(field);
  const url = format({
    pathname: `${basePath}/${fieldName}`,
    query: { retroactive }
  });
  return {
    type: UPDATE_FIELD,
    payload: {
      url,
      schema: FIELD,
      options: {
        method: 'PUT',
        body: field
      }
    }
  };
}

// Delete Field
export const [
  DELETE_FIELD,
  DELETE_FIELD_REQUEST,
  DELETE_FIELD_SUCCESS,
  DELETE_FIELD_FAILURE
] = api.buildTypes('DELETE_FIELD');

export function deleteField(field, processID, stepName) {
  return (dispatch, getState) => {
    const { fieldName } = field;
    const state = getState();
    const process = getEntityByID(state.entities.processes, PROCESS, {
      processID
    });
    if (stepName) {
      const step = getEntityByID(state.entities.steps, STEP, {
        processID,
        stepName
      });
      return dispatch(
        removeFieldFromStepWritableFields(step, process, fieldName)
      );
    } else if (processID) {
      return dispatch(removeFieldFromProcessWritableFields(process, fieldName));
    }
  };
}

export function deleteFieldEntity(field) {
  const { fieldName } = field;
  const basePath = apiPathForField(field);
  return {
    type: DELETE_FIELD,
    payload: {
      url: `${basePath}/${fieldName}`,
      schema: FIELD,
      options: {
        method: 'DELETE'
      }
    }
  };
}

function removeFieldFromProcessWritableFields(process, fieldName) {
  const {
    teamName,
    processID,
    writableFields: previousWritableFields = []
  } = process;
  const writableFields = cloneDeep(previousWritableFields);
  remove(writableFields, thisFieldName => thisFieldName === fieldName);
  return updateProcess({
    teamName,
    processID,
    data: { writableFields }
  });
}

function removeFieldFromStepWritableFields(step, process, fieldName) {
  const {
    teamName,
    stepName,
    writableFields: previousWritableFields = []
  } = step;
  const { processID } = process;
  const writableFields = cloneDeep(previousWritableFields);
  remove(writableFields, thisFieldName => thisFieldName === fieldName);
  return updateStep({
    teamName,
    processName: processID,
    stepName,
    step: { writableFields }
  });
}

// Shared
function apiPathForField(field) {
  const {
    processID,
    stepName, // eslint-disable-line no-unused-vars
    rowID,
    runID,
    tableID,
    taskName,
    teamName
  } = field;
  let path = `/teams/${teamName}`;
  if (tableID) {
    if (rowID) {
      path += `/tables/${tableID}/rows/${rowID}`;
    } else {
      path += `/tables/${tableID}/rows/${tableID}`;
    }
  } else if (runID) {
    path += `/runs/${runID}`;
    if (taskName) {
      path += `/tasks/${taskName}`;
    }
  } else if (processID) {
    path += `/processes/${processID}`;
  }
  path += `/fields`;
  return path;
}



// WEBPACK FOOTER //
// ./js/actions/api/field.js